import React from "react";
import LegalLayout from "./LegalLayout";

const PrivacyPolicy = () => (
  <LegalLayout title="Privacy Policy" lastUpdated="11 July 2026">
    <p>
      Operated by <strong>Pingme Software Design</strong> ("Ping", "we", "us"),
      registered in the United Arab Emirates. Contact:{" "}
      <a href="mailto:tariq@pingme.ae">tariq@pingme.ae</a> ·{" "}
      <a href="https://www.pingme.ae">www.pingme.ae</a>. Applies across all Ping
      products: Ping Stitch, Ping Laundry, Ping Drive, and Ping Paw.
    </p>

    <h2>1. Who we are</h2>
    <p>
      Ping provides WhatsApp-based customer notification and messaging software
      to service businesses in the UAE across four products: Ping Stitch
      (tailors), Ping Laundry (laundries), Ping Drive (automotive workshops),
      and Ping Paw (veterinary and pet services). This policy explains how we
      handle personal data.
    </p>

    <h2>2. The data we handle, and our two roles</h2>
    <p>Ping handles personal data in two distinct capacities:</p>
    <ul>
      <li>
        <strong>As a data controller</strong> — for data about our direct
        business customers (the merchants who subscribe to Ping): names,
        business details, contact information, login credentials, and billing
        information.
      </li>
      <li>
        <strong>As a data processor</strong> — for data that our merchants
        upload or generate about their own customers (for example, a customer's
        name, phone number, order details, and messaging history). This data
        belongs to the merchant; Ping processes it on their behalf and under
        their instructions, solely to provide the service.
      </li>
    </ul>

    <h2>3. What we collect</h2>
    <p>
      Depending on your relationship with Ping, we may process: name and
      contact details; phone numbers; business/trade licence information;
      order, appointment, and transaction records entered into the platform;
      WhatsApp message content sent through the platform; marketing consent
      status; payment and billing details; and technical data such as log data,
      device information, and usage analytics.
    </p>

    <h2>4. How we use personal data</h2>
    <p>
      We use personal data to: provide and operate the Ping platform; send
      WhatsApp notifications and messages on behalf of merchants to their
      customers; process payments and manage subscriptions; provide customer
      support; improve and secure our services; and comply with legal
      obligations.
    </p>

    <h2>5. WhatsApp and third-party processors</h2>
    <p>
      Ping operates on top of the WhatsApp Business Platform (provided by Meta)
      and uses third-party infrastructure and service providers (which may
      include messaging providers such as Twilio, payment processors such as
      Stripe, hosting providers, and analytics tools). Your use of WhatsApp
      messaging is also subject to WhatsApp's and Meta's own terms and
      policies. These providers process data on our behalf under appropriate
      agreements.
    </p>

    <h2>6. Marketing messages and consent</h2>
    <p>
      Marketing messages are only sent to customers who have opted in to
      receive them. Recipients can opt out at any time by replying "STOP" (or a
      similar keyword), after which no further marketing messages will be sent.
      Merchants are responsible for ensuring they have obtained valid consent
      before sending marketing messages to their customers through Ping.
    </p>

    <h2>7. Data sharing</h2>
    <p>We do not sell personal data. We share data only with:</p>
    <ul>
      <li>the merchant to whom the data belongs;</li>
      <li>our third-party processors (Section 5); and</li>
      <li>
        authorities or parties where required by law or to protect our legal
        rights.
      </li>
    </ul>

    <h2>8. International transfers</h2>
    <p>
      Some of our service providers may process data outside the UAE. Where
      this happens, we take steps intended to ensure an appropriate level of
      protection for the data.
    </p>

    <h2>9. Data retention</h2>
    <p>
      We retain personal data for as long as needed to provide the service and
      to meet legal, accounting, or reporting obligations. Merchants may
      request deletion of their data, and customers of merchants may exercise
      their rights via the relevant merchant.
    </p>

    <h2>10. Your rights</h2>
    <p>
      Subject to applicable UAE law (including the Personal Data Protection
      Law), individuals may have rights to access, correct, delete, or restrict
      the processing of their personal data, and to withdraw consent. To
      exercise these rights, contact{" "}
      <a href="mailto:tariq@pingme.ae">tariq@pingme.ae</a>. If your data was
      provided to a merchant, we may direct your request to that merchant as
      the controller of that data.
    </p>

    <h2>11. Security</h2>
    <p>
      We use reasonable technical and organisational measures to protect
      personal data. However, no system is completely secure, and we cannot
      guarantee absolute security.
    </p>

    <h2>12. Children</h2>
    <p>
      Ping is a business-to-business service and is not directed at children.
      We do not knowingly collect data from children.
    </p>

    <h2>13. Changes</h2>
    <p>
      We may update this policy from time to time. The latest version will
      always be available on{" "}
      <a href="https://www.pingme.ae">www.pingme.ae</a>.
    </p>

    <h2>14. Contact</h2>
    <p>
      Questions about this policy:{" "}
      <a href="mailto:tariq@pingme.ae">tariq@pingme.ae</a>.
    </p>
  </LegalLayout>
);

export default PrivacyPolicy;
